import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material'
import {
  AutoAwesomeTwoTone as AutoAwesomeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'

import ThemesList from './ThemesList'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { getThemes, postThemes } from 'redux/thunks/Themes'
import { getIcon, getMessage, getTitle } from '../Helpers/ThemesHelper'

interface Props {
  switchToContributionsTab: () => void
}

const AIThemes = ({ switchToContributionsTab }: Props) => {
  const dispatch = useAppDispatch()
  const [initialLoadDone, setInitialLoadDone] = useState(false)

  const { themes, isExtracting, status } = useAppSelector(
    (state) => state.textAnalysis.theme,
  )
  const {
    post: { loading: postLoading, error: postError, success: postSuccess },
    get: { loading: getLoading, error: getError, success: getSuccess },
  } = status

  const themeList = useMemo(() => themes?.data ?? [], [themes])
  const hasNoThemes = themeList.length === 0
  const isLoading = postLoading || getLoading

  // Handle initial data fetch only once when component mounts
  useEffect(() => {
    if (!initialLoadDone && !isExtracting) {
      dispatch(getThemes())
      setInitialLoadDone(true)
    }
  }, [dispatch, initialLoadDone, isExtracting])

  // Handle theme extraction - POST followed by GET
  const handleExtractThemes = useCallback(async () => {
    const res = await dispatch(postThemes())
    if (res.type.includes('fulfilled')) {
      // GET call after successful POST
      await dispatch(getThemes())
    }
  }, [dispatch])

  // Handle manual reload of themes
  const handleReloadThemes = useCallback(() => {
    dispatch(getThemes())
  }, [dispatch])

  // Reload button inside content
  const showReloadInContent =
    isExtracting && postSuccess && getSuccess && hasNoThemes

  const renderActionButtons = () => (
    <Box
      display="flex"
      justifyContent="flex-end"
      alignItems="center"
      mb={2}
      gap={2}
    >
      <Button
        variant="outlined"
        color="primary"
        startIcon={
          postLoading ? (
            <CircularProgress color="inherit" size={16} />
          ) : (
            <AutoAwesomeIcon />
          )
        }
        onClick={handleExtractThemes}
        disabled={isLoading}
      >
        Extract Themes
      </Button>
    </Box>
  )

  const renderEmptyState = () => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 240,
      }}
    >
      {getIcon({
        postLoading,
        getLoading,
        getError,
        postError,
        isExtracting,
        postSuccess,
        hasNoThemes,
      })}

      <Typography variant="h6" color="text.secondary" gutterBottom>
        {getTitle({
          postLoading,
          getLoading,
          getError,
          postError,
          isExtracting,
          postSuccess,
          hasNoThemes,
        })}
      </Typography>

      <Typography variant="body2" color="text.secondary" align="center">
        {getMessage({
          postLoading,
          getLoading,
          getError,
          postError,
          isExtracting,
          postSuccess,
          getSuccess,
          hasNoThemes,
          initialLoadDone,
        })}
      </Typography>

      {showReloadInContent && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={
            getLoading ? (
              <CircularProgress color="inherit" size={16} />
            ) : (
              <RefreshIcon />
            )
          }
          onClick={handleReloadThemes}
          disabled={getLoading}
          sx={{ mt: 2 }}
        >
          Reload Themes
        </Button>
      )}
    </Paper>
  )

  return (
    <Box sx={{ width: '100%' }} data-testid="ta-themes">
      {renderActionButtons()}
      {hasNoThemes ? (
        renderEmptyState()
      ) : (
        <Grid item xs={12} md={12} lg={9}>
          <ThemesList
            themes={themeList}
            switchToContributionsTab={switchToContributionsTab}
          />
        </Grid>
      )}
    </Box>
  )
}

export default React.memo(AIThemes)
